<template>
    <div class="folder-tree" @dragover="handleAutoScroll">
        <ul class="folder-tree-root">
            <li class="tree-entry" :class="{active: view === 'LIBRARY' && folder === 'all'}"
                @click="$emit('select', { view: 'LIBRARY', folder: 'all' })">
                <i class="fad fa-photo-film-music fa-fw"></i>
                <span class="tree-label">All Media</span>
            </li>
            <li class="tree-entry" :class="{active: view === 'LIBRARY' && folder === 'null'}"
                @click="$emit('select', { view: 'LIBRARY', folder: 'null' })"
                @dragover.prevent="markDrop('null', $event)"
                @dragleave="clearDrop('null')"
                @drop.prevent="onDrop(null, $event)"
                :data-drop-active="dropTargetKey === 'null'">
                <i class="fad fa-folder-xmark fa-fw"></i>
                <span class="tree-label">Unsorted</span>
            </li>
        </ul>

        <div class="tree-section-header"
             :class="{'is-drop-target': isDraggingFolder && dropTargetKey === 'root'}"
             @dragover="onRootDragOver($event)"
             @dragleave="onRootDragLeave"
             @drop.prevent="onRootDrop">
            <span v-if="isDraggingFolder">Drop here to move to root</span>
            <span v-else>Folders</span>
            <a class="tree-new-folder" @click="$emit('create-folder', null)" title="New top-level folder">
                <i class="fas fa-plus"></i>
            </a>
        </div>

        <ul v-if="rootFolders.length" class="folder-tree-nodes">
            <folder-tree-node
                v-for="f in rootFolders"
                :key="f.id"
                :folder="f"
                :children-map="childrenMap"
                :file-counts="fileCounts"
                :expanded-set="expandedSet"
                :selected-id="selectedFolderId"
                :drop-target-id="dropTargetFolderId"
                :depth="0"
                @toggle-expand="toggleExpand"
                @select="onSelectFolder"
                @create-subfolder="(pid) => $emit('create-folder', pid)"
                @rename="(folder) => $emit('rename-folder', folder)"
                @delete="(folder) => $emit('delete-folder', folder)"
                @dragover-folder="onDragOverFolder"
                @dragleave-folder="onDragLeaveFolder"
                @drop-folder="onDropOnFolder"
                @dragstart-folder="onDragStartFolder"
                @dragend-folder="onDragEndFolder"
            />
        </ul>
        <div v-else class="tree-empty is-size-7 has-text-grey">
            No folders yet.
        </div>

        <ul class="folder-tree-root trash-section">
            <li class="tree-entry trash-entry" :class="{active: view === 'TRASH'}"
                @click="$emit('select', { view: 'TRASH', folder: 'all' })">
                <i class="fad fa-trash fa-fw"></i>
                <span class="tree-label">Trash</span>
            </li>
        </ul>
    </div>
</template>

<script>
    import FolderTreeNode from "./FolderTreeNode.vue";

    export default {
        name: 'FolderTree',
        components: { FolderTreeNode },
        props: {
            folders: { type: Array, default: () => [] },
            fileCounts: { type: Object, default: () => ({}) },
            view: { type: String, default: 'LIBRARY' },
            folder: { type: [String, Number], default: 'all' },
            isDraggingFolder: { type: Boolean, default: false }
        },
        data() {
            return {
                expandedSet: {},       // { [folderId]: true }
                dropTargetKey: null,   // 'null' | 'root' | `folder:${id}`
                dropTargetFolderId: null,
                _autoScrollRaf: null,
                _autoScrollSpeed: 0
            };
        },
        watch: {
            selectedFolderId: {
                immediate: true,
                handler(newId) {
                    if (newId) this.expandAncestors(newId);
                }
            },
            folders() {
                // folder hierarchy may arrive after selectedFolderId is already set
                // (restore-from-localStorage flow); re-run once we have data.
                if (this.selectedFolderId) this.expandAncestors(this.selectedFolderId);
            }
        },
        computed: {
            childrenMap() {
                const map = {};
                this.folders.forEach(f => {
                    const p = f.parent_folder_id || null;
                    if (!map[p]) map[p] = [];
                    map[p].push(f);
                });
                Object.keys(map).forEach(k => map[k].sort((a, b) => (a.title || '').localeCompare(b.title || '')));
                return map;
            },
            rootFolders() {
                return this.childrenMap[null] || [];
            },
            selectedFolderId() {
                if (this.view !== 'LIBRARY') return null;
                if (this.folder === 'all' || this.folder === 'null') return null;
                return Number(this.folder);
            }
        },
        methods: {
            toggleExpand(id) {
                this.$set(this.expandedSet, id, !this.expandedSet[id]);
            },
            expandAncestors(id) {
                const byId = {};
                this.folders.forEach(f => { byId[f.id] = f; });
                let current = byId[id];
                while (current && current.parent_folder_id) {
                    this.$set(this.expandedSet, current.parent_folder_id, true);
                    current = byId[current.parent_folder_id];
                }
            },
            onSelectFolder(id) {
                this.$emit('select', { view: 'LIBRARY', folder: id });
            },
            markDrop(key) {
                this.dropTargetKey = key;
            },
            clearDrop(key) {
                if (this.dropTargetKey === key) this.dropTargetKey = null;
            },
            onDrop(folderId) {
                this.dropTargetKey = null;
                this.dropTargetFolderId = null;
                this.$emit('drop-items', { folderId });
            },
            onDragOverFolder(id) {
                this.dropTargetFolderId = id;
                this.dropTargetKey = `folder:${id}`;
            },
            onDragLeaveFolder(id) {
                if (this.dropTargetFolderId === id) this.dropTargetFolderId = null;
                if (this.dropTargetKey === `folder:${id}`) this.dropTargetKey = null;
            },
            onDropOnFolder(payload) {
                this.dropTargetFolderId = null;
                this.dropTargetKey = null;
                this.$emit('drop-items', payload);
            },
            onDragStartFolder(payload) {
                this.$emit('dragstart-folder', payload);
            },
            onDragEndFolder() {
                this.dropTargetFolderId = null;
                this.dropTargetKey = null;
                this.$emit('dragend-folder');
            },
            onRootDragOver(e) {
                if (!this.isDraggingFolder) return;
                e.preventDefault();
                this.dropTargetKey = 'root';
            },
            onRootDragLeave() {
                if (this.dropTargetKey === 'root') this.dropTargetKey = null;
            },
            onRootDrop() {
                if (!this.isDraggingFolder) return;
                this.dropTargetKey = null;
                this.$emit('drop-items', { folderId: null });
            },
            // Auto-scroll the tree while a drag is in progress and the pointer
            // is near the top or bottom edge. HTML5 drag events don't do this
            // for us, and otherwise users can't reach folders that are out of view.
            handleAutoScroll(e) {
                if (!this.$el) return;
                const rect = this.$el.getBoundingClientRect();
                const threshold = 56;
                const maxSpeed = 20;
                const y = e.clientY;
                let speed = 0;
                if (y - rect.top < threshold) {
                    const p = 1 - Math.max(0, y - rect.top) / threshold;
                    speed = -Math.max(1, Math.round(maxSpeed * p));
                } else if (rect.bottom - y < threshold) {
                    const p = 1 - Math.max(0, rect.bottom - y) / threshold;
                    speed = Math.max(1, Math.round(maxSpeed * p));
                }
                this._autoScrollSpeed = speed;
                if (speed !== 0 && !this._autoScrollRaf) this.startAutoScrollLoop();
            },
            startAutoScrollLoop() {
                const step = () => {
                    if (!this.$el || this._autoScrollSpeed === 0) {
                        this._autoScrollRaf = null;
                        return;
                    }
                    this.$el.scrollTop += this._autoScrollSpeed;
                    this._autoScrollRaf = requestAnimationFrame(step);
                };
                this._autoScrollRaf = requestAnimationFrame(step);
            },
            stopAutoScroll() {
                this._autoScrollSpeed = 0;
                if (this._autoScrollRaf) {
                    cancelAnimationFrame(this._autoScrollRaf);
                    this._autoScrollRaf = null;
                }
            }
        },
        mounted() {
            // Safety net: any drag ending anywhere on the page stops the loop,
            // in case the user drops outside the tree or hits Escape.
            this._onGlobalDragEnd = () => this.stopAutoScroll();
            document.addEventListener('dragend', this._onGlobalDragEnd);
            document.addEventListener('drop', this._onGlobalDragEnd);
        },
        beforeDestroy() {
            this.stopAutoScroll();
            if (this._onGlobalDragEnd) {
                document.removeEventListener('dragend', this._onGlobalDragEnd);
                document.removeEventListener('drop', this._onGlobalDragEnd);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .folder-tree {
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 0.5rem 0;
        overflow-y: auto;
    }

    .folder-tree-root {
        list-style: none;
        margin: 0;
        padding: 0;

        &.trash-section {
            margin-top: auto;
            padding-top: 0.5rem;
            border-top: 1px solid #e0e2e8;
        }
    }

    .folder-tree-nodes {
        list-style: none;
        margin: 0;
        padding: 0;
        flex: 1;
    }

    .tree-entry {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.45rem 1rem;
        margin: 0;
        cursor: pointer;
        color: #4a4a4a;
        font-size: 0.9rem;
        transition: background 120ms;

        i {
            color: #3c6691;
            font-size: 1.1rem;
            flex: 0 0 auto;
        }

        .tree-label {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &:hover {
            background: #eef4fb;
        }

        &.active {
            background: #336699;
            color: white;

            i {
                color: white;
            }
        }

        &[data-drop-active="true"] {
            background: #d5ecff;
            outline: 2px dashed #3c6691;
            outline-offset: -2px;
        }

        &.trash-entry i {
            color: #8a99ae;
        }

        &.trash-entry.active i {
            color: white;
        }
    }

    .tree-section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem 0.25rem;
        font-size: 0.72rem;
        text-transform: uppercase;
        font-weight: 700;
        color: #737881;
        letter-spacing: 0.02em;
        transition: background 120ms, color 120ms;

        &.is-drop-target {
            background: #d5ecff;
            color: #336699;
            outline: 2px dashed #3c6691;
            outline-offset: -4px;
        }

        .tree-new-folder {
            color: #737881;
            cursor: pointer;

            &:hover {
                color: #336699;
            }
        }
    }

    .tree-empty {
        padding: 0.4rem 1rem;
    }
</style>
